import { redirect } from 'react-router-dom';
import { authStore, resolveIsAnonymous } from './authStore';
import { getPostAuthPath, getPostStoragePath } from '../utils/onboardingNavigation';
import { isEffectivelyStorageLinked, requiresExternalStorage } from '../utils/storageNavigation';
import { preserveUtmParams } from '../utils/utm';
import { getTracks } from '../services/trackService';
import { listSharedWithMe } from '../services/shareService';

function isAuthed(snap: ReturnType<typeof authStore.getSnapshot>) {
  return snap.status === 'authenticated' && !!snap.user;
}

function storageState(snap: ReturnType<typeof authStore.getSnapshot>) {
  return {
    userType: snap.userType,
    includesCassetteCloud: snap.includesCassetteCloud,
    driveLinked: snap.driveLinked,
    storageProvider: snap.storageProvider,
    isAnonymous: resolveIsAnonymous(snap),
  };
}

function postAuthRedirect(snap: ReturnType<typeof authStore.getSnapshot>) {
  const state = storageState(snap);
  return redirect(
    preserveUtmParams(
      getPostAuthPath(
        snap.isNewUser,
        isEffectivelyStorageLinked(state),
        state.isAnonymous,
        requiresExternalStorage(state),
      ),
    ),
  );
}

/** `/` index: send authenticated users to their post-auth destination before paint. */
export async function rootIndexLoader() {
  const snap = await authStore.whenReady();
  if (isAuthed(snap)) {
    return postAuthRedirect(snap);
  }
  return null;
}

/** Protected app shell (Layout): require an authenticated user. */
export async function protectedLoader() {
  const snap = await authStore.whenReady();
  if (!isAuthed(snap)) {
    return redirect(preserveUtmParams('/auth/login'));
  }
  return null;
}

/** `/auth/login`: bounce already-authenticated users to their post-auth destination. */
export async function guestLoader() {
  const snap = await authStore.whenReady();
  if (isAuthed(snap)) {
    return postAuthRedirect(snap);
  }
  return null;
}

/** `/auth/storage`: skip the picker if storage is already linked or the user is anonymous. */
export async function storageLoader() {
  const snap = await authStore.whenReady();
  if (!isAuthed(snap)) {
    return redirect(preserveUtmParams('/auth/login'));
  }
  const state = storageState(snap);
  if (state.isAnonymous || isEffectivelyStorageLinked(state)) {
    return redirect(preserveUtmParams(getPostStoragePath(snap.isNewUser)));
  }
  return null;
}

export interface DashboardLoaderData {
  tracks: Promise<Awaited<ReturnType<typeof getTracks>>>;
  shared: Promise<Awaited<ReturnType<typeof listSharedWithMe>>>;
}

/**
 * Dashboard: auth is awaited (so the redirect decision is resolved before paint),
 * but track data is returned as still-pending promises so the Layout shell paints
 * instantly and the content area fills in (good perceived perf on slow mobile).
 */
export async function dashboardLoader(): Promise<Response | DashboardLoaderData> {
  const snap = await authStore.whenReady();
  if (!isAuthed(snap)) {
    return redirect(preserveUtmParams('/auth/login'));
  }
  if (!isEffectivelyStorageLinked(storageState(snap))) {
    return { tracks: Promise.resolve([]), shared: Promise.resolve([]) };
  }
  return {
    tracks: getTracks().catch(() => []),
    shared: listSharedWithMe().catch(() => []),
  };
}
